/**
 * PeriodPicker — Sélecteur de période bancaire (Mois / Semaine / Jour)
 * ====================================================================
 * PM 24/02/2026 — Permet de naviguer aisément entre les mois pour :
 *  - Consulter le solde et les KPIs mois par mois
 *  - Clôturer chaque période en un clic
 *  - Zoomer sur une semaine ou un jour spécifique (filtre côté frontend)
 *
 * Props :
 *   value: string YYYY-MM       — période mois active
 *   onChange: (p: string) => void
 *   granularity: 'month' | 'week' | 'day'  (default: 'month')
 *   onGranularityChange: (g) => void
 *   subPeriod: string (YYYY-MM-DD pour day, YYYY-Www pour week) — pour zoom
 *   onSubPeriodChange: (sp) => void
 *   closureStatus?: 'ready' | 'review' | 'blocked' | 'closed'  — badge visuel
 */
import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Lock } from 'lucide-react';

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                   'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const prevMonth = (yyyymm) => {
  const [y, m] = yyyymm.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const nextMonth = (yyyymm) => {
  const [y, m] = yyyymm.split('-').map(Number);
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const CLOSURE_BADGES = {
  ready:   { label: 'Prêt pour clôture', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  review:  { label: 'À vérifier',        cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  blocked: { label: 'Clôture bloquée',   cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  closed:  { label: 'Clôturé',           cls: 'bg-slate-200 text-slate-700 border-slate-300' },
};

export default function PeriodPicker({
  value,
  onChange,
  granularity = 'month',
  onGranularityChange,
  subPeriod,
  onSubPeriodChange,
  closureStatus,
  onOpenCloseDialog,
}) {
  const [y, m] = (value || '').split('-').map(Number);
  const label = (y && m) ? `${MONTHS_FR[m - 1]} ${y}` : '—';
  const now = new Date();
  const isCurrent = y === now.getFullYear() && m === (now.getMonth() + 1);
  const badge = closureStatus ? CLOSURE_BADGES[closureStatus] : null;

  // Générer une liste rapide des 12 derniers mois pour dropdown
  const quickPeriods = useMemo(() => {
    const out = [];
    let refY = now.getFullYear();
    let refM = now.getMonth() + 1;
    for (let i = 0; i < 18; i++) {
      out.push(`${refY}-${String(refM).padStart(2, '0')}`);
      refM -= 1;
      if (refM === 0) { refM = 12; refY -= 1; }
    }
    return out;
  }, [now]);

  const formatPeriodOpt = (p) => {
    const [yy, mm] = p.split('-').map(Number);
    return `${MONTHS_FR[mm - 1]} ${yy}`;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between flex-wrap gap-3"
         data-testid="bank-period-picker">
      {/* Bloc navigation mois */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onChange(prevMonth(value))}
          className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-600"
          title="Mois précédent"
          data-testid="period-prev-btn"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-emerald-600" />
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Période affichée</span>
            <select
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="text-base font-bold text-slate-900 bg-transparent border-0 focus:ring-0 cursor-pointer -ml-1 py-0"
              data-testid="period-select"
            >
              {quickPeriods.map(p => (
                <option key={p} value={p}>{formatPeriodOpt(p)}</option>
              ))}
            </select>
          </div>
        </div>

        <button
          onClick={() => onChange(nextMonth(value))}
          className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-600"
          title="Mois suivant"
          data-testid="period-next-btn"
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        {!isCurrent && (
          <button
            onClick={() => {
              const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
              onChange(cur);
            }}
            className="ml-1 px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
            data-testid="period-today-btn"
          >
            Mois courant
          </button>
        )}

        {badge && (
          <span className={`ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${badge.cls}`}>
            <Lock className="h-3 w-3" /> {badge.label}
          </span>
        )}
      </div>

      {/* Granularité (semaine / jour) */}
      <div className="flex items-center gap-2">
        <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
          {[
            { k: 'month', l: 'Mois' },
            { k: 'week',  l: 'Semaine' },
            { k: 'day',   l: 'Jour' },
          ].map(g => (
            <button
              key={g.k}
              onClick={() => onGranularityChange?.(g.k)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                granularity === g.k ? 'bg-white shadow text-emerald-700' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid={`period-granularity-${g.k}`}
            >
              {g.l}
            </button>
          ))}
        </div>

        {granularity === 'week' && (
          <input
            type="week"
            value={subPeriod || ''}
            onChange={(e) => onSubPeriodChange?.(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1"
            data-testid="period-sub-week"
          />
        )}
        {granularity === 'day' && (
          <input
            type="date"
            value={subPeriod || ''}
            onChange={(e) => onSubPeriodChange?.(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1"
            data-testid="period-sub-day"
          />
        )}

        {onOpenCloseDialog && (
          <button
            onClick={onOpenCloseDialog}
            className="ml-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1.5"
            data-testid="period-close-btn"
            title={`Clôturer ${label}`}
          >
            <Lock className="h-3.5 w-3.5" /> Clôturer {label}
          </button>
        )}
      </div>
    </div>
  );
}
